import React, { ReactNode, useEffect } from "react";
import { MCard, MGrid, MSpinner } from "components/Shared";
import { getAiVerificationResults, getAiVerificationFormResults } from "actions/homeowner/caseforms";
import { connect } from "react-redux";
import { InitialState } from "reducers/initialState";
import { MGridColumnDefinition } from "components/Shared/MGrid";
import { CaseDataVerificationDto } from "interfaces/Mueller.Business.Models";
import { dateTimeToString } from "components/Shared/Formatters";
import { DATA_TYPE_BOOLEAN } from "typeConstants/dataTypes";

interface Props {
  controlNumber: number;
  aiVerification: InitialState["caseForms"]["aiVerification"];
  aiVerificationForm: InitialState["caseForms"]["aiVerificationForm"];
  getAIVerificationResults: (controlNumber: number) => void;
  getAIVerificationFormResults: (controlNumber: number) => void;
}

const excludedDataPoints = ["Roof Style", "Exterior Wall Material", "Exterior Roof Material", "Foundation Type"];

const AIResultsTable = ({
  controlNumber,
  aiVerification,
  aiVerificationForm,
  getAIVerificationResults,
  getAIVerificationFormResults,
}: Props) => {
  useEffect(() => {
    if (aiVerification.controlNumber !== controlNumber) {
      getAIVerificationResults(controlNumber);
    }
    if (aiVerificationForm.controlNumber !== controlNumber) {
      getAIVerificationFormResults(controlNumber);
    }
  }, [controlNumber]);

  let colDefs: MGridColumnDefinition<CaseDataVerificationDto>[] = [
    { key: "dataPoint", name: "Data Point", dataSource: (v) => v.verificationCategory.secondaryCategoryName },
    { key: "verificationType", name: "Verification Type", dataSource: (v) => v.verificationCategory.mainCategoryName },
    { key: "frValue", name: "FR Value", dataSource: "observedValue" },
    { key: "aiVlaue", name: "AI Value", dataSource: "verificationValue" },
    { key: "match", name: "Match", dataSource: "valuesAreConsideredEquivalent", dataType: DATA_TYPE_BOOLEAN },
  ];

  let inner: ReactNode = null;

  if (
    aiVerification.fetchingResults ||
    aiVerificationForm.fetchingResults ||
    !aiVerification.results ||
    !aiVerificationForm.results
  ) {
    inner = <MSpinner />;
  } else if (aiVerification.results?.length === 0) {
    inner = "No AI Results Available";
  } else {
    let passedVerificationFormNames = aiVerificationForm.results
      .filter((r) => r.verificationPassed)
      .map((r) => r.availFormName);
    let failedVerificationFormNames = aiVerificationForm.results
      .filter((r) => !r.verificationPassed)
      .map((r) => r.availFormName);
    inner = (
      <>
        <MCard title="Data Verification Summary" sx={{ height: "auto" }} cardContentClassName="no-scroll">
          Reviewed by AI on {dateTimeToString(aiVerification.results[0].verificationDateUtc)}
          {passedVerificationFormNames.length > 0 && (
            <>
              <br />
              The following forms have been verified by AI and do not need to be reviewed by QA:{" "}
              {passedVerificationFormNames.join("; ")}
            </>
          )}
          {failedVerificationFormNames.length > 0 && (
            <>
              <br />
              The following forms have been reviewed by AI and need to be reviewed by QA:{" "}
              {failedVerificationFormNames.join("; ")}
            </>
          )}
        </MCard>
        <MGrid
          cardStyle
          data={aiVerification.results?.filter(
            (r) => !excludedDataPoints.includes(r.verificationCategory.secondaryCategoryName),
          )}
          columnDefinitions={colDefs}
        />
      </>
    );
  }

  return <div className="ai-results-table">{inner}</div>;
};

const mapStateToProps = (state) => ({
  aiVerification: state.caseForms.aiVerification,
  aiVerificationForm: state.caseForms.aiVerificationForm,
});

const mapDispatchToProps = (dispatch) => ({
  getAIVerificationResults: (controlNumber: number) => dispatch(getAiVerificationResults(controlNumber)),
  getAIVerificationFormResults: (controlNumber: number) => dispatch(getAiVerificationFormResults(controlNumber)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AIResultsTable);
